/**
 * InsuranceManagement.jsx - Insurance policy management page
 * 
 * Features:
 * - List policies with status badges
 * - Add/Edit policy modal
 * - PDF upload
 * - Coverage warnings (EU 2019/947 minimum)
 * - Filter by status
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import {
  Shield, Plus, Edit2, Trash2, FileText, Upload, AlertTriangle,
  CheckCircle, Clock, XCircle, ExternalLink, Filter, Building2,
  Calendar, Euro, Plane, Loader2, X, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import cs from '../locales/cs';
import en from '../locales/en';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useOrganizationScope } from '../contexts/OrganizationScopeContext';

const EU_MINIMUM_COVERAGE = 750000;

export default function InsuranceManagement() {
  const { getAuthHeaders } = useAuth();
  const { t: globalT, language } = useLanguage();
  const t = language === 'cs' ? cs.insurance : en.insurance;
  const { registerScopeChangeGuard } = useOrganizationScope();

  const [policies, setPolicies] = useState([]);
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [aircraft, setAircraft] = useState([]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    policy_number: '',
    insurer: '',
    coverage_type: 'both',
    coverage_amount: '',
    valid_from: '',
    valid_until: '',
    aircraft_ids: [],
    notes: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Fetch policies
  const fetchPolicies = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? `?status_filter=${statusFilter}` : '';
      const [policiesRes, statusRes] = await Promise.all([
        api.get(`/insurance${params}`, ),
        api.get(`/insurance/status`, )
      ]);
      setPolicies(policiesRes.data);
      setStatusData(statusRes.data);
    } catch (err) {
      console.error('Failed to fetch policies:', err);
      toast.error(globalT.errors?.failedToLoadInsurance || 'Failed to load insurance policies');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, globalT]);

  // Fetch aircraft for multi-select
  const fetchAircraft = useCallback(async () => {
    try {
      const res = await api.get(`/aircraft`, );
      setAircraft(res.data.aircraft || res.data || []);
    } catch (err) {
      console.error('Failed to fetch aircraft:', err);
    }
  }, []);

  useEffect(() => {
    fetchPolicies();
    fetchAircraft();
  }, [fetchPolicies, fetchAircraft]);

  useEffect(() => {
    if (!showModal) return undefined;
    return registerScopeChangeGuard(() => window.confirm(globalT.organizationScope?.formDirtyConfirm));
  }, [showModal, registerScopeChangeGuard, globalT.organizationScope?.formDirtyConfirm]);

  // Open add modal
  const handleAdd = () => {
    setEditingPolicy(null);
    setForm({
      policy_number: '',
      insurer: '',
      coverage_type: 'both',
      coverage_amount: '',
      valid_from: '',
      valid_until: '',
      aircraft_ids: [],
      notes: ''
    });
    setUploadFile(null);
    setShowModal(true);
  };

  // Open edit modal
  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setForm({
      policy_number: policy.policy_number,
      insurer: policy.insurer,
      coverage_type: policy.coverage_type,
      coverage_amount: policy.coverage_amount.toString(),
      valid_from: policy.valid_from.split('T')[0],
      valid_until: policy.valid_until.split('T')[0],
      aircraft_ids: policy.aircraft_ids || [],
      notes: policy.notes || ''
    });
    setUploadFile(null);
    setShowModal(true);
  };

  // Save policy
  const handleSave = async () => {
    if (!form.policy_number || !form.insurer || !form.coverage_amount || !form.valid_from || !form.valid_until) {
      toast.error(globalT.common?.fillRequiredFields || (language === 'cs' ? 'Vyplňte všechna povinná pole' : 'Fill in all required fields'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        coverage_amount: parseFloat(form.coverage_amount),
        valid_from: new Date(form.valid_from).toISOString(),
        valid_until: new Date(form.valid_until).toISOString()
      };

      let policyId;
      if (editingPolicy) {
        await api.put(`/insurance/${editingPolicy.id}`, payload, );
        policyId = editingPolicy.id;
        toast.success(t.policyUpdated);
      } else {
        const res = await api.post(`/insurance`, payload, );
        policyId = res.data.id;
        toast.success(t.policyCreated);
      }

      // Upload PDF if selected
      if (uploadFile && policyId) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', uploadFile);
        await api.post(`/insurance/${policyId}/upload`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
        });
        toast.success(t.documentUploaded);
      }

      setShowModal(false);
      fetchPolicies();
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(err.response?.data?.detail || globalT.errors?.failedToUpdate || 'Failed to save policy');
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  // Delete policy
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/insurance/${deleteConfirm.id}`, );
      toast.success(t.policyDeleted);
      setDeleteConfirm(null);
      fetchPolicies();
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(globalT.errors?.failedToDelete || 'Failed to delete policy');
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(language === 'cs' ? 'cs-CZ' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-US');
  };

  // Get status badge
  const getStatusBadge = (policy) => {
    if (policy.status === 'valid') {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200 gap-1">
          <CheckCircle className="h-3 w-3" />
          {t.valid}
        </Badge>
      );
    }
    if (policy.status === 'expiring_soon') {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1">
          <Clock className="h-3 w-3" />
          {t.expiringSoon}
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-700 border-red-200 gap-1">
        <XCircle className="h-3 w-3" />
        {t.expired}
      </Badge>
    );
  };

  // Get coverage type label
  const getCoverageTypeLabel = (type) => {
    if (type === 'liability') return t.liability;
    if (type === 'hull') return t.hull;
    return t.both;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="insurance-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)] flex items-center gap-2">
            <Shield className="h-6 w-6 text-[#1E3A5F]" />
            {t.title}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">{t.subtitle}</p>
        </div>
        <Button onClick={handleAdd} className="gap-2 bg-[#1E3A5F] hover:bg-[#2a4a73]" data-testid="add-policy-btn">
          <Plus className="h-4 w-4" />
          {t.addPolicy}
        </Button>
      </div>

      {/* Stats Cards */}
      {statusData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[var(--text-muted)]">{t.totalPolicies}</p>
                  <p className="text-2xl font-bold text-[var(--text-primary)]">{statusData.total_policies}</p>
                </div>
                <Shield className="h-8 w-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[var(--text-muted)]">{t.validPolicies}</p>
                  <p className="text-2xl font-bold text-green-600">{statusData.valid_policies}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[var(--text-muted)]">{t.expiringPolicies}</p>
                  <p className="text-2xl font-bold text-amber-600">{statusData.expiring_policies}</p>
                </div>
                <Clock className="h-8 w-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[var(--text-muted)]">{t.expiredPolicies}</p>
                  <p className="text-2xl font-bold text-red-600">{statusData.expired_policies}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Coverage Warning */}
      {statusData && !statusData.has_sufficient_coverage && statusData.total_policies > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">{t.coverageWarning}</p>
            <p className="text-xs text-amber-600 mt-1">{t.coverageMinimum}</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-[var(--text-muted)]" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter">
            <SelectValue placeholder={t.filterByStatus} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.allStatuses}</SelectItem>
            <SelectItem value="valid">{t.valid}</SelectItem>
            <SelectItem value="expiring_soon">{t.expiringSoon}</SelectItem>
            <SelectItem value="expired">{t.expired}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Policies Table */}
      {policies.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-[var(--text-primary)]">{t.noPolicies}</h3>
            <p className="text-sm text-[var(--text-muted)] mt-1">{t.noPoliciesDesc}</p>
            <Button onClick={handleAdd} className="mt-4 gap-2" variant="outline">
              <Plus className="h-4 w-4" />
              {t.addPolicy}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.policyNumber}</th>
                    <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.insurer}</th>
                    <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.coverageType}</th>
                    <th className="text-right text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.coverageAmount}</th>
                    <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.validUntil}</th>
                    <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.coveredAircraft}</th>
                    <th className="text-left text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.status}</th>
                    <th className="text-right text-xs font-medium text-[var(--text-muted)] px-4 py-3">{t.actions}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {policies.map((policy) => (
                    <tr key={policy.id} className="hover:bg-slate-50" data-testid={`policy-row-${policy.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-medium text-[var(--text-primary)]">{policy.policy_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-[var(--text-muted)]" />
                          <span className="text-sm text-[var(--text-secondary)]">{policy.insurer}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">
                          {getCoverageTypeLabel(policy.coverage_type)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${policy.coverage_amount < EU_MINIMUM_COVERAGE ? 'text-amber-600' : 'text-[var(--text-primary)]'}`}>
                          {formatCurrency(policy.coverage_amount)}
                        </span>
                        {policy.coverage_amount < EU_MINIMUM_COVERAGE && (
                          <AlertTriangle className="h-3 w-3 text-amber-500 inline ml-1" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-[var(--text-muted)]" />
                          <span className="text-sm text-[var(--text-secondary)]">{formatDate(policy.valid_until)}</span>
                        </div>
                        <span className="text-xs text-[var(--text-muted)]">
                          {policy.days_until_expiry >= 0 
                            ? `${policy.days_until_expiry} ${t.daysRemaining}`
                            : `${Math.abs(policy.days_until_expiry)} ${t.daysAgo}`
                          }
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {policy.aircraft_ids?.length > 0 ? (
                          <Badge variant="outline" className="gap-1">
                            <Plane className="h-3 w-3" />
                            {policy.aircraft_ids.length}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            {t.allAircraft}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(policy)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {policy.document_url && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${policy.document_url}`, '_blank')}
                              title={t.viewPdf}
                            >
                              <FileText className="h-4 w-4 text-blue-600" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleEdit(policy)}
                            data-testid={`edit-policy-${policy.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                            onClick={() => setDeleteConfirm(policy)}
                            data-testid={`delete-policy-${policy.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-[#1E3A5F]" />
              {editingPolicy ? t.editPolicy : t.addPolicy}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="policy_number">{t.policyNumber} *</Label>
                <Input
                  id="policy_number"
                  value={form.policy_number}
                  onChange={(e) => setForm({ ...form, policy_number: e.target.value })}
                  placeholder="POL-2026-001"
                  data-testid="policy-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="insurer">{t.insurer} *</Label>
                <Input
                  id="insurer"
                  value={form.insurer}
                  onChange={(e) => setForm({ ...form, insurer: e.target.value })}
                  placeholder="Allianz"
                  data-testid="insurer-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.coverageType} *</Label>
                <Select 
                  value={form.coverage_type} 
                  onValueChange={(v) => setForm({ ...form, coverage_type: v })}
                >
                  <SelectTrigger data-testid="coverage-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="liability">{t.liability}</SelectItem>
                    <SelectItem value="hull">{t.hull}</SelectItem>
                    <SelectItem value="both">{t.both}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="coverage_amount">{t.coverageAmount} (EUR) *</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-muted)]" />
                  <Input
                    id="coverage_amount"
                    type="number"
                    value={form.coverage_amount}
                    onChange={(e) => setForm({ ...form, coverage_amount: e.target.value })}
                    className="pl-9"
                    placeholder="750000"
                    data-testid="coverage-amount-input"
                  />
                </div>
                {form.coverage_amount && parseFloat(form.coverage_amount) < EU_MINIMUM_COVERAGE && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {t.coverageWarning}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valid_from">{t.validFrom} *</Label>
                <Input
                  id="valid_from"
                  type="date"
                  value={form.valid_from}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  data-testid="valid-from-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valid_until">{t.validUntil} *</Label>
                <Input
                  id="valid_until"
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                  data-testid="valid-until-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t.coveredAircraft}</Label>
              <div className="flex flex-wrap gap-2 p-2 border rounded-lg bg-gray-50 max-h-32 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, aircraft_ids: [] })}
                  className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                    form.aircraft_ids.length === 0
                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {t.allAircraft}
                </button>
                {aircraft.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        aircraft_ids: prev.aircraft_ids.includes(a.id)
                          ? prev.aircraft_ids.filter((id) => id !== a.id)
                          : [...prev.aircraft_ids, a.id],
                      }));
                    }}
                    className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                      form.aircraft_ids.includes(a.id)
                        ? 'bg-blue-100 border-blue-300 text-blue-700'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Plane className="h-3 w-3 inline mr-1" />
                    {a.registration} - {a.model}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[var(--text-muted)]">
                {form.aircraft_ids.length > 0
                  ? `${form.aircraft_ids.length} ${language === 'cs' ? 'vybráno' : 'selected'}`
                  : t.selectAircraft}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t.notes}</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                data-testid="notes-input"
              />
            </div>

            {/* PDF Upload */}
            <div className="space-y-2">
              <Label>{t.uploadPdf}</Label>
              <div 
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  uploadFile ? 'border-green-300 bg-green-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => document.getElementById('pdf-upload')?.click()}
              >
                <input
                  id="pdf-upload"
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2 text-green-700">
                    <FileText className="h-5 w-5" />
                    <span className="text-sm font-medium">{uploadFile.name}</span>
                    <button 
                      onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}
                      className="ml-2 text-red-500 hover:text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : editingPolicy?.document_url ? (
                  <div className="text-slate-600">
                    <FileText className="h-6 w-6 mx-auto mb-1" />
                    <p className="text-sm">{t.pdfUploaded}</p>
                    <p className="text-xs text-blue-600 mt-1">{t.replacePdf}</p>
                  </div>
                ) : (
                  <div className="text-slate-500">
                    <Upload className="h-6 w-6 mx-auto mb-1" />
                    <p className="text-sm">{t.dropPdfHere}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              {language === 'cs' ? 'Zrušit' : 'Cancel'}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving || uploading}
              className="bg-[#1E3A5F] hover:bg-[#2a4a73]"
              data-testid="save-policy-btn"
            >
              {(saving || uploading) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {language === 'cs' ? 'Uložit' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.deletePolicy}</AlertDialogTitle>
            <AlertDialogDescription>
              {t.deleteConfirm}
              <br />
              <span className="text-red-600">{t.deleteWarning}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'cs' ? 'Zrušit' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {language === 'cs' ? 'Smazat' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
